if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) {
          console.error('Product form not found');
          return;
        }
        
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('new-cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        
        if (!this.submitButton) {
          console.error('Submit button not found');
          return;
        }
        
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('new-cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

      }

      getProductMetafield(fieldName = 'product_variant_color') {
        // first try dataset attr added via Liquid
        if (this.dataset && this.dataset.productVariantColor) {
          return this.dataset.productVariantColor;
        }
        // Extract product-level metafield from page JSON data; fall back to global ProductJSON script if necessary
        let productDataScript = this.closest('product-form')?.querySelector('script[type="application/json"]');
        if (!productDataScript) {
          // look for global script tag whose id starts with 'ProductJSON-'
          productDataScript = document.querySelector('script[id^="ProductJSON"]');
        }
        if (productDataScript && productDataScript.textContent) {
          try {
            const productData = JSON.parse(productDataScript.textContent);
            if (productData.metafields && productData.metafields.custom && productData.metafields.custom[fieldName]) {
              return productData.metafields.custom[fieldName];
            }
          } catch (e) {
            console.log('Could not parse product metafield:', e);
          }
        }
        return '';
      }

      getColorNameFromVariant(variantId) {
        // Try multiple ways to get the color name from variant data
        
        // Method 1: Check for data attributes on variant elements
        const variantElement = this.querySelector(`[data-variant-id="${variantId}"]`);
        if (variantElement && variantElement.dataset.colorName) {
          return variantElement.dataset.colorName;
        }
        
        // Method 2: Look for color swatches or color selector elements with matching variant ID
        const colorSelectors = this.querySelectorAll('[data-color-name][data-variant-id]');
        for (const selector of colorSelectors) {
          if (selector.getAttribute('data-variant-id') === variantId) {
            return selector.getAttribute('data-color-name');
          }
        }
        
        // Method 3: Check variant title field if it contains color info
        const variantSelector = this.querySelector('select[name="id"]');
        if (variantSelector) {
          const selectedOption = variantSelector.querySelector(`option[value="${variantId}"]`);
          if (selectedOption) {
            // Try to extract color from variant title (common pattern: "Color - Size")
            const title = selectedOption.textContent;
            const parts = title.split('-');
            if (parts.length > 1) {
              return parts[0].trim(); // Return the color part
            }
          }
        }
        
        // Method 4: Look in the document for product variant data stored in script tags
        const productDataScript = this.closest('product-form')?.querySelector('script[type="application/json"]');
        if (productDataScript && productDataScript.textContent) {
          try {
            const productData = JSON.parse(productDataScript.textContent);
            const variant = productData.variants?.find(v => String(v.id) === String(variantId));
            if (variant) {
              // Try metafield first
              if (variant.metafields && variant.metafields.custom && variant.metafields.custom.product_variant_color) {
                return variant.metafields.custom.product_variant_color;
              }
              // Look for color in variant options or generic properties
              return variant.color || variant.option1 || variant.option2 || '';
            }
          } catch (e) {
            console.log('Could not parse product data:', e);
          }
        }
        
        return '';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        this.handleErrorMessage();

        this.submitButton.classList.add('loading');
        this.submitButton.setAttribute('aria-disabled', 'true');
        const buttonsContainer = this.querySelector('.product-form__buttons');
        if (buttonsContainer) buttonsContainer.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.remove('hidden');
        }

       
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (!response) {
              console.error('Empty response from cart add');
              this.handleErrorMessage('Failed to add item to cart');
              return;
            }
            
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });

              // also update bubble directly for immediate visual feedback
              if (typeof updateCartIconBubble === 'function') {
                updateCartIconBubble(response.item_count);
              } else {
                // fallback to manual DOM update if the helper isn't defined yet
                const bubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
                if (bubble) {
                  const current = parseInt(bubble.textContent) || 0;
                  bubble.textContent = current + 1;
                  const vis = bubble.parentElement.querySelector('.visually-hidden');
                  if (vis) vis.textContent = (current + 1) + ' items';
                }
              }

              // Update all buttons to "Added" for 3 seconds
              const getBtnTextNodesLocal = (btn) => {
                const textWrapInners = Array.from(btn.querySelectorAll('.text-wrap .text'));
                if (textWrapInners.length > 0) return textWrapInners;
                const spans = Array.from(btn.querySelectorAll('span:not(.button_price):not(.sale-price):not(.regular-price)'));
                if (spans.length > 0) return [spans[0]];
                return [];
              };
              
              const allSubmitBtns = document.querySelectorAll('.product-form__submit:not(.size-chart-update-btn), .custom_product-form__submit:not(.size-chart-update-btn)');
              const allOriginalTexts = new Map();
              
              allSubmitBtns.forEach(btn => {
                const textNodes = getBtnTextNodesLocal(btn);
                const originals = [];
                textNodes.forEach(n => {
                  originals.push(n.innerHTML);
                  if (n.textContent.trim() !== 'Added') {
                    n.innerHTML = n.innerHTML.replace(n.textContent.trim(), 'Added');
                  }
                });
                allOriginalTexts.set(btn, { nodes: textNodes, originals });
              });

              setTimeout(() => {
                allSubmitBtns.forEach(btn => {
                  const data = allOriginalTexts.get(btn);
                  if (data) {
                    data.nodes.forEach((n, idx) => {
                      if (n.textContent.trim() === 'Added') {
                        n.innerHTML = data.originals[idx];
                      }
                    });
                  }
                });
              }, 3000);

              // Show mobile bottom sheet cart popup on mobile devices
              console.log('AJAX Response received:', response);
              console.log('Window width:', window.innerWidth);


              if (window.innerWidth <= 768) {

                const mobileDrawer = document.querySelector('new-cart-drawer');

                if (mobileDrawer) {

                  // render cart first
                  if (typeof mobileDrawer.renderContents === 'function') {  
                    mobileDrawer.renderContents(response);
                  }

                  // wait one frame so browser can paint
                  requestAnimationFrame(() => {

                    mobileDrawer.classList.add('animate');

                    requestAnimationFrame(() => {
                      mobileDrawer.classList.add('active');
                    });

                  });

                }

              }
              
              // if (window.innerWidth <= 768) {
              //   const bottomSheet = document.querySelector('cart-bottom-sheet');
              //   console.log('Bottom sheet element found:', !!bottomSheet);
                
              //   if (bottomSheet) {
              //     // The response itself is the added item (not response.items)
              //     const addedItem = response;
              //     console.log('Added item data:', addedItem);
                  
              //     // determine color name: preference order
              //     // 1. property passed with cart line
              //     // 2. color provided via data attribute on product-form
              //     // 3. variant-based fallback
              //     const pageColor = this.dataset.colorName || this.form.dataset.colorName || '';
              //     // determine color used in product title (breadcrumb etc)
              //     const color_name = addedItem.properties?.product_color_name || addedItem.properties?.Color ||
              //                        pageColor || this.getColorNameFromVariant(formData.get('id')) || '';

              //     // get variant color/label for bottom sheet; prefer data attr, then page metafield
              //     let variant_color = this.dataset.productVariantColor || this.getProductMetafield('product_variant_color') || '';
              //     // if still empty, try form data or variant metafield from AJAX response
              //     if (!variant_color) {
              //       if (addedItem.variant && addedItem.variant.metafields && addedItem.variant.metafields.custom && addedItem.variant.metafields.custom.product_variant_color) {
              //         variant_color = addedItem.variant.metafields.custom.product_variant_color;
              //       } else if (addedItem.properties?.product_variant_color) {
              //         variant_color = addedItem.properties.product_variant_color;
              //       } else {
              //         variant_color = this.getColorNameFromVariant(formData.get('id')) || '';
              //       }
              //     }
              //     // keep size separate; variant_title already holds the selected option
              //     const size_title = addedItem.variant_title || '';

              //     // Prepare product data from AJAX response
              //     const featured_image = addedItem.image || 
              //                           (addedItem.featured_image ? {src: addedItem.featured_image.src, alt: addedItem.featured_image.alt} : null);

              //     const productData = {
              //       featured_image: featured_image,
              //       product_title: addedItem.product_title || addedItem.title || '',
              //       color_name: color_name,
              //       variant_title: size_title,
              //       product_variant_color: variant_color,
              //       price: addedItem.final_price ? 
              //         (addedItem.final_price / 100).toLocaleString('en-IN', { 
              //           style: 'currency', 
              //           currency: 'INR',
              //           maximumFractionDigits: 0
              //         }) : 
              //         addedItem.price ? (addedItem.price / 100).toLocaleString('en-IN', { 
              //           style: 'currency', 
              //           currency: 'INR',
              //           maximumFractionDigits: 0
              //         }) : ''
              //     };
                  
              //     document.dispatchEvent(
              //       new CustomEvent('cartBottomSheetShow', {
              //         detail: productData
              //       })
              //     );

              //     console.log('Prepared product data:', productData);
              //     console.log('Dispatching cartBottomSheetShow event');
                  
              //     // Dispatch custom event to show bottom sheet
              //     const event = new CustomEvent('cartBottomSheetShow', { detail: productData });
              //     document.dispatchEvent(event);
              //     console.log('Event dispatched successfully');
              //   }
              // }

              // Always refresh cart sections (bubble + drawer contents) even though we won't open drawer
              if (typeof refreshCart === 'function') {
                refreshCart();
              }
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  // Modal closed; nothing extra to do since we don't show drawer
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              // Open cart drawer only on desktop
              if (window.innerWidth > 768) {
                if (this.cart && typeof this.cart.renderContents === 'function') {
                  this.cart.renderContents(response);
                }
              }
            }
          })
          .catch((e) => {
            console.error('Error adding to cart:', e);
            this.handleErrorMessage('Failed to add item to cart. Please try again.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            const buttonsContainer = this.querySelector('.product-form__buttons');
            if (buttonsContainer) buttonsContainer.classList.remove('loading');
            if (this.cart && this.cart.classList && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            const spinner = this.querySelector('.loading__spinner');
            if (spinner) {
              spinner.classList.add('hidden');
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          //this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
